import { EEventsType, EPageSection, EPublisherEvents } from './enums';
import { enrichOptions, getAnalyticsEnvironment } from './eventDataUtils';
import { trackMixpanelEvent } from './mixpanelClient';
import { PageRedirectConfig } from './types';

export const PAGE_CLICK_TRACKED_CONTAINER_SELECTOR =
  '[data-testid="PortalBlock-Container"], [data-testid="PortalFooter-Container"]';

export const PAGE_CLICK_ELEMENT_ATTR = 'data-track-element';
export const PAGE_CLICK_ELEMENT_TYPE_ATTR = 'data-track-type';
export const PAGE_CLICK_SECTION_ATTR = 'data-page-section';
export const PAGE_CLICK_STORE_REDIRECT_ATTR = 'data-store-redirect';
export const PAGE_CLICK_REDIRECT_URL_ATTR = 'data-redirect-url';

export const getRedirectTrackingAttrs = (isRedirect: boolean, redirectConfig?: PageRedirectConfig) => ({
  [PAGE_CLICK_STORE_REDIRECT_ATTR]: isRedirect ? redirectConfig?.isStoreRedirect ?? false : undefined,
  [PAGE_CLICK_REDIRECT_URL_ATTR]:
    isRedirect && redirectConfig?.isStoreRedirect ? redirectConfig?.redirectUrl : undefined,
});

const TRACKABLE_ATTRS_SELECTOR = `[${PAGE_CLICK_ELEMENT_ATTR}], [data-testid], [id]`;

const CONTENT_ROUTE_SECTIONS: Array<{ prefix: string; section: EPageSection }> = [
  { prefix: '/store', section: EPageSection.CONTENT_STORE },
  { prefix: '/content', section: EPageSection.CONTENT_ARTICLE },
];

const matchesRoutePrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const resolveContentPageSection = (pathname: string): EPageSection => {
  const matched = CONTENT_ROUTE_SECTIONS.find(({ prefix }) => matchesRoutePrefix(pathname, prefix));
  if (matched) return matched.section;
  if (pathname === '/') return EPageSection.CONTENT_HOME;
  return EPageSection.CONTENT_PAGE;
};

interface HandlePageClickParams {
  target: EventTarget | null;
  pathname: string;
  currentPageName?: string;
  platform?: string;
  playerId?: string;
  trackPublisherUIEvent: {
    mutate: (data: any) => void;
  };
}

export const handlePageClick = ({
  target,
  pathname,
  currentPageName,
  platform,
  playerId,
  trackPublisherUIEvent,
}: HandlePageClickParams) => {
  if (!(target instanceof Element)) return;

  if (target.closest(PAGE_CLICK_TRACKED_CONTAINER_SELECTOR)) return;

  const describedElement = target.closest(TRACKABLE_ATTRS_SELECTOR);
  const sectionElement = target.closest(`[${PAGE_CLICK_SECTION_ATTR}]`);
  const redirectElement = target.closest(`[${PAGE_CLICK_STORE_REDIRECT_ATTR}]`);

  const clickedElement =
    describedElement?.getAttribute(PAGE_CLICK_ELEMENT_ATTR) ||
    describedElement?.getAttribute('data-testid') ||
    describedElement?.id ||
    target.tagName.toLowerCase();

  const clickedElementType =
    describedElement?.getAttribute(PAGE_CLICK_ELEMENT_TYPE_ATTR) ||
    describedElement?.tagName.toLowerCase() ||
    target.tagName.toLowerCase();

  const rawPageSection = sectionElement?.getAttribute(PAGE_CLICK_SECTION_ATTR) || EPageSection.PAGE;
  const pageSection =
    rawPageSection === EPageSection.CONTENT
      ? resolveContentPageSection(pathname)
      : rawPageSection;

  const isStoreRedirect = redirectElement?.getAttribute(PAGE_CLICK_STORE_REDIRECT_ATTR) === 'true';
  const redirectUrl = isStoreRedirect
    ? redirectElement?.getAttribute(PAGE_CLICK_REDIRECT_URL_ATTR) || undefined
    : undefined;

  const block = {
    type: clickedElementType,
    items: [{ isStoreRedirect, redirectUrl }],
  };

  trackMixpanelEvent(EEventsType.PORTAL_PAGE_CLICKED, {
    block,
    'sessionMetaData.pageUrl': pathname,
    'sessionMetaData.pageName': currentPageName || '',
    'sessionMetaData.appVersion': process.env.NEXT_PUBLIC_APP_VERSION,
    player_id: playerId,
    device_platform: platform || '',
    clicked_element: clickedElement,
    clicked_element_type: clickedElementType,
    page_section: pageSection,
    environment: getAnalyticsEnvironment(),
  });

  trackPublisherUIEvent.mutate({
    ...enrichOptions({
      platform: platform || '',
      eventName: EPublisherEvents.PORTAL_PAGE_CLICKED,
      block,
      page: [
        {
          pageUrl: pathname,
          pageName: currentPageName || '',
        },
      ],
      playerId: playerId,
      clickedElement,
      clickedElementType,
      pageSection,
    }),
  });
};
